import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, signal } from "@angular/core";
import { IDatepickerComponent, IDateRange, ISelectMenuImports } from "@front/m19-ui";
import { TranslocoService } from "@jsverse/transloco";
import { Comparison, DateUtils } from "@front/m19-utils";
import { takeUntilDestroyed, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { AuthService, UserSelectionService } from "@front/m19-services";
import { combineLatest, map, Observable } from "rxjs";
import { DateTime } from "luxon";
import { filter, withLatestFrom } from "rxjs/operators";

export interface ComparisonOption {
  startDate?: DateTime;
  endDate?: DateTime;
  label: string;
  formattedRange?: string;
  value: Comparison;
}

export const AvailableComparisonPeriods: { label: string; value: Comparison }[] = [
  {
    label: "date-range-selection.no_comparison",
    value: Comparison.None,
  },
  {
    label: "date-range-selection.previous_period",
    value: Comparison.PreviousPeriod,
  },
  {
    label: "date-range-selection.previous_week",
    value: Comparison.PreviousWeek,
  },
  {
    label: "date-range-selection.previous_month",
    value: Comparison.PreviousMonth,
  },
  {
    label: "date-range-selection.previous_year",
    value: Comparison.Previousyear,
  },
  {
    label: "common.custom",
    value: Comparison.Custom,
  },
];

@Component({
  selector: "app-period-comparison",
  imports: [ISelectMenuImports, IDatepickerComponent],
  templateUrl: "./period-comparison.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PeriodComparisonComponent implements OnInit {
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly authService = inject(AuthService);
  private readonly translocoService = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly Comparison = Comparison;

  allowCustomComparison = input<boolean>(true);

  readonly user = toSignal(this.authService.user$);

  readonly dateFormat = computed(() => DateUtils.getDateFormatString(this.user()?.locale ?? "en-US"));
  readonly selectedDateRange$: Observable<IDateRange> = this.userSelectionService.selectedDateRange$.pipe(
    map((dateRange) => {
      return {
        start: dateRange.start ? dateRange.start.startOf("day") : DateTime.utc().startOf("day"),
        end: dateRange.end.startOf("day"),
      };
    }),
  );
  readonly selectedDateRange = toSignal<IDateRange>(this.selectedDateRange$, { requireSync: true });
  readonly comparisonPeriodOptions = computed<ComparisonOption[]>(() => {
    return AvailableComparisonPeriods.filter((c) => this.allowCustomComparison() || c.value !== Comparison.Custom).map(
      (c) =>
        buildComparisonOption(
          c.value,
          this.translocoService.translate(c.label),
          this.dateFormat(),
          this.selectedDateRange(),
        ),
    );
  });
  readonly selectedComparison = toSignal(this.userSelectionService.periodComparison$, { requireSync: true });
  readonly selectedMenuValue = computed(() => this.selectedComparison()?.type ?? Comparison.None);
  readonly selectedOptionDisplay = computed(() =>
    this.comparisonPeriodOptions().find((o) => o.value === this.selectedMenuValue()),
  );
  readonly comparisonPeriodOptions$ = toObservable(this.comparisonPeriodOptions);
  readonly customComparisonStartDate = signal<IDateRange | null>(null);
  readonly isSettingCustomComparison = signal(false);

  ngOnInit() {
    combineLatest([this.userSelectionService.periodComparison$, this.selectedDateRange$])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(([periodComparison, _]) => periodComparison?.type === Comparison.Custom),
      )
      .subscribe(([periodComparison, selectedDateRange]) => {
        const comparisonStartDate = periodComparison?.period?.[0];
        if (comparisonStartDate && selectedDateRange.start <= DateTime.fromISO(comparisonStartDate).toUTC()) {
          this.selectPeriodComparison(Comparison.PreviousPeriod);
          this.isSettingCustomComparison.set(false);
        }
      });

    this.comparisonPeriodOptions$
      .pipe(withLatestFrom(this.userSelectionService.periodComparison$), takeUntilDestroyed(this.destroyRef))
      .subscribe(([_, option]) => {
        if (option) this.selectPeriodComparison(option.type);
      });
  }

  selectPeriodComparison(comparison: Comparison | undefined): void {
    if (comparison == null) return;

    if (comparison === Comparison.Custom && !this.isSettingCustomComparison()) {
      this.isSettingCustomComparison.set(true);
      return;
    }

    let comparisonOption: ComparisonOption | undefined;
    if (comparison === Comparison.Custom) {
      comparisonOption = this.getCustomComparisonOption();
    } else {
      comparisonOption = this.comparisonPeriodOptions().find((o) => o.value === comparison);
      this.isSettingCustomComparison.set(false);
    }

    this.setPeriodComparisonDateRange(comparisonOption);
  }

  selectCustomComparisonDate(date: IDateRange | null) {
    this.customComparisonStartDate.set(date);
    this.selectPeriodComparison(Comparison.Custom);
  }

  private getCustomComparisonOption(): ComparisonOption | undefined {
    const option = this.comparisonPeriodOptions().find((o) => o.value === Comparison.Custom);
    if (!option) return undefined;

    const newOption = { ...option };

    const selectedStartDate = this.selectedDateRange().start;
    const selectedEndDate = this.selectedDateRange().end;

    if (!selectedEndDate) return newOption;

    const startDate = this.customComparisonStartDate()?.start;
    if (!startDate) return newOption;

    const dateInterval = selectedEndDate?.diff(selectedStartDate, "days");
    newOption.startDate = DateTime.fromISO(startDate.toISO()!);
    newOption.endDate = startDate.plus(dateInterval);
    return newOption;
  }

  private setPeriodComparisonDateRange(selectedComparison: ComparisonOption | undefined) {
    if (selectedComparison) {
      const periodComparison = selectedComparison.startDate
        ? selectedComparison.endDate
          ? [selectedComparison.startDate, selectedComparison.endDate]
          : [selectedComparison.startDate]
        : undefined;
      this.userSelectionService.togglePeriodComparison(
        selectedComparison?.value !== Comparison.None ? periodComparison : undefined,
        selectedComparison?.value,
      );
    }
  }
}

export function buildComparisonOption(
  comparison: Comparison,
  label: string,
  dateFormat: string,
  selectedDateRange: IDateRange,
): ComparisonOption {
  const periodComparisonDate = getPeriodComparisonDate(comparison, selectedDateRange);

  const formattedStartDate = periodComparisonDate.startDate ? periodComparisonDate.startDate.toFormat(dateFormat) : "";
  const formattedEndDate = periodComparisonDate.endDate ? periodComparisonDate.endDate.toFormat(dateFormat) : "";
  const formattedRange = formattedStartDate && formattedEndDate ? ` (${formattedStartDate} - ${formattedEndDate})` : "";

  return {
    label,
    formattedRange,
    value: comparison,
    startDate: periodComparisonDate.startDate,
    endDate: periodComparisonDate.endDate,
  };
}

export function getPeriodComparisonDate(
  comparison: Comparison,
  selectedDateRange: IDateRange,
): {
  startDate?: DateTime;
  endDate?: DateTime;
} {
  const selectedStartDate = selectedDateRange.start;
  const selectedEndDate = selectedDateRange.end;

  if (!selectedStartDate || !selectedEndDate) {
    return {
      startDate: undefined,
      endDate: undefined,
    };
  }

  const dayInterval = selectedEndDate.diff(selectedStartDate, "days");

  let startDate: DateTime | undefined;
  let endDate: DateTime | undefined;
  switch (comparison) {
    case Comparison.None:
      startDate = undefined;
      endDate = undefined;
      break;
    case Comparison.PreviousWeek:
      startDate = selectedStartDate.minus({ day: 7 });
      break;
    case Comparison.PreviousMonth:
      startDate = selectedStartDate.minus({ month: 1 });
      break;
    case Comparison.Previousyear:
      startDate = selectedStartDate.minus({ year: 1 });
      break;
    case Comparison.PreviousPeriod:
      startDate = selectedStartDate.minus(dayInterval.plus({ day: 1 }));
      break;
  }

  if (startDate) endDate = startDate.plus(dayInterval);

  return {
    startDate: startDate ? startDate : undefined,
    endDate: startDate ? endDate : undefined,
  };
}
